/**
 * One date picker, three ways to the same two dates — list engine v3 §5,
 * `design/mockups/38-list-engine-v3/html/list-05-dates.html`.
 *
 * There were three controls for one question. A row of preset chips in the
 * quick filter, a seventeen-item dropdown in the advanced builder, and two
 * date boxes behind a "Custom…" chip — and none of them could show you what
 * the other two were saying. "Last 30 days" did not tell you it meant Aug 23
 * to Sep 21; picking Aug 23 to Sep 21 did not tell you it would still mean
 * those two days next March.
 *
 * So: presets on the left, a calendar in the middle, a count-back row at the
 * foot, **all three showing the same resolved range at once**. Whichever you
 * touch, the other two follow. The one thing the surface insists on telling
 * you is the difference between the two kinds of answer, because it is
 * invisible in the words and it is what people are surprised by six months
 * later: a preset and a count-back **move with today**, two dates on the
 * calendar are **fixed**.
 *
 * What is stored, and nothing else is:
 *   preset            → `in_period { preset }`
 *   calendar / inputs → `in_period { from, to }`
 *   count back        → `in_relative { unit, amount, direction }`
 *   Is empty / not    → `is_null` / `is_not_null`
 *
 * The month grid is local rather than `date-field.tsx`'s `Calendar`: that one
 * is a single month in its own positioned dialog, anchored to a field, with
 * the APG keyboard model of a date *picker*. This is two months inline inside
 * a panel, painting a range across both. They share the arithmetic
 * (`shared/src/calendar.ts`) and nothing else, which is the right amount.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DATE_PRESET_GROUPS, DATE_PRESET_LABELS, RELATIVE_UNITS, addDays, datePeriod,
  describeDayRange, describePeriod, describeRelative, parseDay, periodDays, relativeDays,
  isFilterGroup, isRelatedCondition, relativeWindow, startOf,
  type DatePreset, type FilterCondition, type RelativeUnit, type RelativeWindow,
} from '@rowted/shared'
import { Button, SectionLabel } from './primitives.js'
import { Popover } from './overlays.js'
import { PanelSheet, SheetButton } from './panel-sheet.js'
import { MenuSelect } from './menu-select.js'
import { DateField } from './date-field.js'
import {
  IconCalendar, IconCheck, IconClock, IconClose, IconNext,
} from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { usePhone } from '../lib/use-narrow.js'
import type { UseListResult } from '../lib/use-list.js'

/*
 * The rail is `DATE_PRESET_GROUPS` — Past · Now · Coming, then everything
 * else behind "More windows ›" — and the grouping lives in shared beside the
 * labels so the picker cannot offer a preset nothing can name, and a new
 * preset cannot be added without a place to put it.
 */
const RAIL = DATE_PRESET_GROUPS.filter((group) => group.label !== 'More')
const MORE = DATE_PRESET_GROUPS.find((group) => group.label === 'More')?.presets ?? []

export interface DatePickerProps {
  /** The column this is about — the header, and the lock copy. */
  label: string
  /** The condition's field name, which is what gets written back. */
  field: string
  value: FilterCondition | null
  onChange: (next: FilterCondition | null) => void
  onClose: () => void
  /** The viewer's today, so a preset resolves here the way it will there. */
  today: string
  /** "118 of 422 match", when the caller is measuring. */
  count?: { shown: number; total: number } | undefined
  /** One month instead of two, for the phone sheet. */
  oneMonth?: boolean
}

/** The picker in a popover, anchored to whatever opened it. */
export function DatePicker({
  anchor, ...rest
}: DatePickerProps & { anchor: HTMLElement | null }) {
  return (
    <Popover
      open
      onClose={rest.onClose}
      anchor={anchor}
      width={700}
      label={`${rest.label}: pick dates`}
    >
      <DatePickerBody {...rest} chrome />
    </Popover>
  )
}

/**
 * The panel itself, without a container, so the phone sheet can hold the same
 * thing (§6: on a phone every panel is the same bottom sheet).
 */
export function DatePickerBody({
  label, field, value, onChange, onClose, today, count, oneMonth, chrome,
}: DatePickerProps & {
  /**
   * Draw the 44px header and the footer.
   *
   * Off inside a `PanelSheet`, which brings its own of both — a sheet with
   * the picker's header under its own would be two titles, and its footer
   * under the sheet's own Done would be two Dones.
   */
  chrome?: boolean
}) {
  const [showMore, setShowMore] = useState(false)

  const period = value?.op === 'in_period' ? datePeriod.safeParse(value.value) : null
  const preset = period?.success && 'preset' in period.data ? period.data.preset : null
  const window = value?.op === 'in_relative' ? relativeWindow.safeParse(value.value) : null

  /** The two days whatever is set resolves to, which is what the calendar paints. */
  const range = useMemo((): { from: string | null; to: string | null } => {
    if (period?.success) return periodDays(period.data, today)
    if (window?.success) return relativeDays(window.data, today)
    if (value?.op === 'between' && Array.isArray(value.value)) {
      return { from: String(value.value[0] ?? '') || null, to: String(value.value[1] ?? '') || null }
    }
    if (value?.op === 'gte') return { from: String(value.value ?? '') || null, to: null }
    if (value?.op === 'lte') return { from: null, to: String(value.value ?? '') || null }
    return { from: null, to: null }
  }, [period, window, value, today])

  const set = (op: FilterCondition['op'], next: unknown) =>
    onChange({ field, op, ...(next === undefined ? {} : { value: next }) })

  /*
   * Clicking a day. The first click starts a range, the second ends it, and
   * an earlier second click swaps the two rather than refusing — nobody
   * means "no range" by clicking backwards.
   */
  const [anchorDay, setAnchorDay] = useState<string | null>(null)
  const pickDay = (day: string) => {
    if (anchorDay === null) {
      setAnchorDay(day)
      set('in_period', { from: day, to: day })
      return
    }
    const [from, to] = anchorDay <= day ? [anchorDay, day] : [day, anchorDay]
    setAnchorDay(null)
    set('in_period', { from, to })
  }

  const typed = (which: 'from' | 'to', day: string) => {
    const other = which === 'from' ? range.to : range.from
    if (!day && !other) { onChange(null); return }
    if (!day) { set(which === 'from' ? 'lte' : 'gte', other); return }
    if (!other) { set(which === 'from' ? 'gte' : 'lte', day); return }
    const [from, to] = which === 'from' ? [day, other] : [other, day]
    set('in_period', from <= to ? { from, to } : { from: to, to: from })
  }

  /*
   * The count-back row's own state, because the number is typed a digit at a
   * time and `{days, 3}` is a filter nobody asked for on the way to 30.
   */
  const [backText, setBackText] = useState(() => String(window?.success ? window.data.amount : 30))
  const backUnit: RelativeUnit = window?.success ? window.data.unit : 'days'
  const countBack = (amountText: string, unit: RelativeUnit) => {
    setBackText(amountText)
    const amount = Number(amountText)
    if (!Number.isInteger(amount) || amount < 1 || amount > 3650) return
    set('in_relative', { unit, amount, direction: 'past' } satisfies RelativeWindow)
  }
  const backPreview = ((): string => {
    const amount = Number(backText)
    if (!Number.isInteger(amount) || amount < 1 || amount > 3650) return ''
    const days = relativeDays({ unit: backUnit, amount, direction: 'past' }, today)
    return describeDayRange(days.from, days.to, today)
  })()

  /** Which month each grid shows: the range's start, or this month. */
  const firstMonth = startOf(range.from ?? today, 'month')
  const [monthShift, setMonthShift] = useState(0)

  const railRow = (one: DatePreset) => {
    const on = preset === one
    const days = periodDays({ preset: one }, today)
    return (
      <button
        key={one}
        type="button"
        onClick={() => set('in_period', { preset: one })}
        className={cn('flex h-[30px] items-center gap-2 rounded-button px-3 text-left text-cell',
          on ? 'bg-accent-soft font-bold text-accent-dark' : 'font-medium text-text hover:bg-hover')}
      >
        <span className="truncate">{DATE_PRESET_LABELS[one]}</span>
        {on && (
          <span className="ml-auto shrink-0 text-[11px] font-normal text-text-faint">
            {describeDayRange(days.from, days.to, today)}
          </span>
        )}
      </button>
    )
  }

  const nullaryRow = (op: 'is_null' | 'is_not_null', text: string) => (
    <button
      type="button"
      onClick={() => set(op, undefined)}
      className={cn('flex h-[30px] items-center rounded-button px-3 text-left text-cell',
        value?.op === op
          ? 'bg-accent-soft font-bold text-accent-dark'
          : 'font-medium text-text hover:bg-hover')}
    >
      {text}
    </button>
  )

  return (
    /*
     * A column whose **middle scrolls**, not whose middle pushes.
     *
     * `Popover` caps its height to the room it has, and this panel is tall:
     * twenty-one presets in a rail and two months beside them. Opened from
     * low on the screen it was capped at 584px, and with the body sized to
     * its content the two rows the rail ends with — Is empty, Is not empty —
     * and the whole footer, Clear and Done with it, were simply outside the
     * box. A panel that is cut off at the bottom is a control somebody
     * cannot finish using, and `overflow-hidden` on the popover means there
     * is no scrollbar to tell them so.
     */
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {chrome && (
        <div className="flex h-11 shrink-0 items-center gap-2 border-b border-line px-4">
          <span className="text-text-muted"><IconCalendar size={15} /></span>
          <b className="text-card">{label}</b>
          <span className="ml-auto text-cell font-semibold text-accent-dark">
            {currentWords(value, today)}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-6 w-6 items-center justify-center rounded-chip text-text-faint hover:bg-hover"
          >
            <IconClose size={13} />
          </button>
        </div>
      )}

      <div className="flex min-h-0 min-w-0 flex-1">
        <div className="no-scrollbar flex w-[200px] shrink-0 flex-col overflow-y-auto border-r border-line bg-surface-alt px-2 pb-2.5 pt-1">
          {RAIL.map((group) => (
            <div key={group.label} className="flex flex-col">
              <div className="px-3 pb-1 pt-2.5"><SectionLabel>{group.label}</SectionLabel></div>
              {group.presets.map(railRow)}
            </div>
          ))}

          {showMore
            ? MORE.map(railRow)
            : (
              <button
                type="button"
                onClick={() => setShowMore(true)}
                className="flex h-[30px] items-center gap-1 rounded-button px-3 text-left text-cell font-medium text-text-muted hover:bg-hover"
              >
                More windows
                <span className="text-text-faint">›</span>
              </button>
            )}

          <span aria-hidden className="mx-2 mb-1 mt-2 h-px bg-line-soft" />
          {nullaryRow('is_null', 'Is empty')}
          {nullaryRow('is_not_null', 'Is not empty')}
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3.5">
          <div className="flex items-end gap-2.5">
            <DateField
              size="field"
              label="From"
              display="long"
              value={range.from ?? ''}
              onChange={(next) => typed('from', next)}
              className="min-w-0 flex-1"
            />
            <span aria-hidden className="pb-2 text-text-faint"><IconNext size={14} /></span>
            <DateField
              size="field"
              label="To"
              display="long"
              value={range.to ?? ''}
              onChange={(next) => typed('to', next)}
              className="min-w-0 flex-1"
            />
          </div>

          <div className="flex gap-5">
            <MonthGrid
              month={addMonthsOf(firstMonth, monthShift)}
              range={range}
              today={today}
              onPick={pickDay}
              onStep={setMonthShift}
              shift={monthShift}
              canStepBack
            />
            {!oneMonth && (
              <MonthGrid
                month={addMonthsOf(firstMonth, monthShift + 1)}
                range={range}
                today={today}
                onPick={pickDay}
                onStep={setMonthShift}
                shift={monthShift}
                canStepForward
              />
            )}
          </div>

          <div className="flex items-center gap-2 rounded-field border border-line bg-bg px-3 py-2.5 text-cell">
            <span className="shrink-0 text-text-muted">
              {oneMonth ? 'In the last' : 'Or count back · in the last'}
            </span>
            <input
              value={backText}
              onChange={(event) => countBack(event.target.value.replace(/\D/g, ''), backUnit)}
              inputMode="numeric"
              aria-label="How many"
              className="h-[30px] w-14 rounded-control border border-line bg-surface px-2 text-center text-field font-semibold tnum text-text"
            />
            <MenuSelect
              label="Unit"
              value={backUnit}
              onChange={(next) => countBack(backText, next as RelativeUnit)}
              options={RELATIVE_UNITS.map((unit) => ({ value: unit, label: unit }))}
              minWidth={86}
            />
            {backPreview && (
              <span className="ml-auto shrink-0 text-[12px] text-text-faint">
                → {backPreview}
              </span>
            )}
          </div>

          <div className="flex items-start gap-2 text-[12px] text-text-muted">
            <span className="shrink-0 pt-px text-accent"><IconClock size={14} /></span>
            <span>
              <b className="text-text">Moves with today.</b>
              {' '}
              A saved view that says “{DATE_PRESET_LABELS.last_30_days}” always means the
              last 30 days. Picking exact dates on the calendar fixes them.
            </span>
          </div>
        </div>
      </div>

      {chrome && (
      <div className="flex shrink-0 items-center gap-2 border-t border-line bg-surface-alt px-3.5 py-2.5">
        {count && (
          <span className="flex items-center gap-1.5 text-[12px] text-text-muted">
            <i aria-hidden className="h-[7px] w-[7px] rounded-full bg-accent" />
            {formatNumber(count.shown)} of {formatNumber(count.total)} match · live
          </span>
        )}
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-3 text-cell font-semibold text-text-muted hover:text-text"
        >
          Clear
        </button>
        <span className="ml-auto">
          <Button variant="primary" onClick={onClose}>
            <IconCheck size={15} />
            Done
          </Button>
        </span>
      </div>
      )}
    </div>
  )
}

/** The words the header's right-hand side says, which are the chip's words. */
function currentWords(value: FilterCondition | null, today: string): string {
  if (!value) return 'Any date'
  if (value.op === 'is_null') return 'Is empty'
  if (value.op === 'is_not_null') return 'Is not empty'
  if (value.op === 'in_period') {
    const period = datePeriod.safeParse(value.value)
    return period.success ? describePeriod(period.data, today) : 'A date window'
  }
  if (value.op === 'in_relative') {
    const window = relativeWindow.safeParse(value.value)
    return window.success ? capitalise(describeRelative(window.data)) : 'A date window'
  }
  return 'Some dates'
}

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

/** `addMonths` on the first of a month, which is all this needs. */
const addMonthsOf = (month: string, count: number): string => {
  const { year, month: index } = parseDay(month)
  const zero = year * 12 + (index - 1) + count
  return `${String(Math.floor(zero / 12)).padStart(4, '0')}-${String((zero % 12) + 1).padStart(2, '0')}-01`
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const MONTH_NAME = new Intl.DateTimeFormat('en-US', {
  month: 'long', year: 'numeric', timeZone: 'UTC',
})

/**
 * One month, with the range painted across it.
 *
 * Six rows from the Sunday on or before the first, so the grid never changes
 * height between months and the two side by side stay level.
 */
function MonthGrid({
  month, range, today, onPick, onStep, shift, canStepBack, canStepForward,
}: {
  month: string
  range: { from: string | null; to: string | null }
  today: string
  onPick: (day: string) => void
  onStep: (shift: number) => void
  shift: number
  canStepBack?: boolean
  canStepForward?: boolean
}) {
  const first = `${month.slice(0, 7)}-01`
  const weekday = new Date(`${first}T12:00:00Z`).getUTCDay()
  const start = addDays(first, -weekday)
  const days = Array.from({ length: 42 }, (_, index) => addDays(start, index))

  return (
    <div className="flex w-[224px] flex-col gap-1">
      <div className="flex h-7 items-center">
        {canStepBack && (
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => onStep(shift - 1)}
            className="flex h-6 w-6 items-center justify-center rounded-chip text-text-faint hover:bg-hover"
          >
            ‹
          </button>
        )}
        <div className="flex-1 text-center text-row font-bold">
          {MONTH_NAME.format(new Date(`${first}T12:00:00Z`))}
        </div>
        {canStepForward && (
          <button
            type="button"
            aria-label="Next month"
            onClick={() => onStep(shift + 1)}
            className="flex h-6 w-6 items-center justify-center rounded-chip text-text-faint hover:bg-hover"
          >
            ›
          </button>
        )}
      </div>

      <div className="grid grid-cols-7">
        {WEEKDAYS.map((letter, index) => (
          <span
            key={index}
            aria-hidden
            className="h-6 text-center text-tag font-semibold leading-6 text-text-faint"
          >
            {letter}
          </span>
        ))}

        {days.map((day) => {
          if (day.slice(0, 7) !== month.slice(0, 7)) return <span key={day} />
          const isStart = day === range.from
          const isEnd = day === range.to
          const inside = range.from !== null && range.to !== null
            && day > range.from && day < range.to
          return (
            <span key={day} className="flex h-[34px] items-center justify-center">
              <button
                type="button"
                onClick={() => onPick(day)}
                aria-pressed={isStart || isEnd}
                className={cn(
                  'flex h-[30px] w-[30px] items-center justify-center rounded-full text-cell tnum',
                  day > today && 'text-text-faint',
                  day === today && 'ring-1 ring-accent-line',
                  inside && 'bg-accent-soft text-accent-dark',
                  (isStart || isEnd) && 'bg-accent font-semibold text-white',
                )}
              >
                {Number(day.slice(8))}
              </button>
            </span>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The one way in
// ---------------------------------------------------------------------------

/**
 * The control that opens the picker: a button reading what is set, and the
 * picker over it — a `Popover` on a desktop, a `PanelSheet` on a phone (§6:
 * every panel on a phone is the same bottom sheet).
 *
 * One component rather than two call sites, because the quick filter's date
 * section and the advanced builder's value cell are the same question asked
 * in two widths, and the thing this replaced was three controls that could
 * not agree about what they were set to.
 */
export function DateWindowButton({
  list, label, field, value, onChange, placeholder = 'Pick dates…',
}: {
  list: UseListResult
  label: string
  field: string
  value: FilterCondition | null
  onChange: (next: FilterCondition | null) => void
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLSpanElement>(null)
  const phone = usePhone()
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const count = useWindowCount(list, open ? value : null)

  const body = (
    <DatePickerBody
      label={label}
      field={field}
      value={value}
      onChange={onChange}
      onClose={() => setOpen(false)}
      today={today}
      count={count}
      oneMonth={phone}
      chrome={!phone}
    />
  )

  return (
    <span ref={box} className="relative flex min-w-0">
      <Button
        variant={value ? 'pressed' : 'default'}
        onClick={() => setOpen((current) => !current)}
        className="min-w-0 flex-1 justify-start gap-1.5"
      >
        <IconCalendar size={14} />
        <span className="min-w-0 truncate">
          {value ? currentWords(value, today) : placeholder}
        </span>
      </Button>

      {open && (phone
        ? (
          <PanelSheet
            open
            onClose={() => setOpen(false)}
            layer={70}
            title={label}
            sub={currentWords(value, today)}
            footer={(
              <>
                {/* Clear lives beside Done, because the sheet's body has no
                    footer of its own — `panelReset()`'s rule for every
                    phone panel. */}
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="shrink-0 px-1 text-cell font-semibold text-text-muted"
                >
                  Clear
                </button>
                <SheetButton onClick={() => setOpen(false)}>Done</SheetButton>
              </>
            )}
          >
            {body}
          </PanelSheet>
        )
        : (
          <Popover
            open
            onClose={() => setOpen(false)}
            anchor={box.current}
            width={700}
            label={`${label}: pick dates`}
          >
            {body}
          </Popover>
        ))}
    </span>
  )
}

/**
 * How many rows this window would leave, and how many there are.
 *
 * Debounced at 200ms for `useDetachedCount`'s reason: without it this is a
 * round trip per keystroke in the count-back box, and the answers come back
 * out of order — the count for "3" arriving after the count for "30" leaves
 * the wrong number under a window nobody chose.
 *
 * It substitutes this one condition into the list's own filter rather than
 * counting it alone, because "118 of 422" has to mean *this list* narrowed by
 * this window, not every row in the tenant that happens to fall in it.
 */
function useWindowCount(
  list: UseListResult, condition: FilterCondition | null,
): { shown: number; total: number } | undefined {
  const [shown, setShown] = useState<number | null>(null)
  const shape = JSON.stringify(condition ?? null)

  useEffect(() => {
    if (!condition) { setShown(null); return undefined }
    /*
     * This list's other conditions, with any earlier one about the same
     * column dropped — the window being chosen replaces it rather than
     * narrowing it, which is also what `setFieldCondition` does.
     */
    const rest = (list.view.filter?.conditions ?? []).filter((node) => {
      if (isFilterGroup(node) || isRelatedCondition(node)) return true
      return node.field !== condition.field
    })
    let cancelled = false
    const timer = setTimeout(() => {
      void list.countMatching({ match: 'and', conditions: [...rest, condition] })
        .then((result) => { if (!cancelled) setShown(result) })
        .catch(() => { if (!cancelled) setShown(null) })
    }, 200)
    return () => { cancelled = true; clearTimeout(timer) }
    // `shape` rather than `condition`: a fresh object every render would make
    // this fire forever.
  }, [shape])

  return shown === null ? undefined : { shown, total: list.totalCount }
}
